// Libraries
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';
// Views
import AddTimeTrackingButton from '../topBar/AddTimeTrackingButton';
// Constants
import { FINALIZE_ALERTS, FINALIZE_STATE_ALERTS, USER_BILLING_STATUSES, DATE_FORMAT, TIME_FORMAT } from '../../constants/constants';
import { ENROLLED_STATUSES } from '../../constants/statuses';
// Hooks
import useLoadPatientCarePlan from '../../hooks/services/useLoadPatientCarePlan';
import useLoadPatientInfo from '../../hooks/services/useLoadPatientInfo';
import useLoadFinalizeAlerts from '../../hooks/services/useLoadFinalizeAlerts';

export const Finalize = () => {
  const { id: patientId } = useParams();
  const [alerts, setAlerts] = useState([]);
  const { refetch } = useLoadPatientCarePlan({ patientId, shouldPatientBeUpdated: true });
  useLoadPatientInfo({ patientId });
  const { patient, timezone, user: { reviewRequired } } = useSelector(state => state);
  const [carePlanHasNoGoalsOrInterventions, setCarePlanHasNoGoalsOrInterventions] = useState(false);
  const billingIsOnHold = patient && patient.billing && patient.billing.sendingStatus === 'ON_HOLD';
  const billingIsNotConfirmed = patient?.billing?.issues?.includes('BILLING_PROBLEMS_ARE_NOT_CONFIRMED');
  const isCSDoNotBill = patient && patient.billingSettings && patient.billingSettings.doNotBill && patient.status === 'CS';
  const { finalizeAlerts, refetch: refetchAlerts } = useLoadFinalizeAlerts({ patientId });

  useEffect(() => {
    setCarePlanHasNoGoalsOrInterventions(patient.hasInterventions === 0 && patient.hasGoals === 0);
  }, [patient]);

  useEffect(() => {
    let combinedAlerts = [];
    
    if (billingIsOnHold || isCSDoNotBill) {
      combinedAlerts.push(FINALIZE_STATE_ALERTS.billingOnHold);
    }

    if (carePlanHasNoGoalsOrInterventions) {
      combinedAlerts.push(FINALIZE_STATE_ALERTS.noGoalsOrInterventions);
    }

    if (reviewRequired) {
      combinedAlerts.push(FINALIZE_STATE_ALERTS.reviewRequired);
    }

    if (billingIsNotConfirmed) {
      combinedAlerts.push(FINALIZE_STATE_ALERTS.billingNotConfirmed);
    }

    if (ENROLLED_STATUSES.includes(patient.status)) {
      Object.keys(finalizeAlerts).forEach((key) => {
        if (finalizeAlerts[key] && FINALIZE_ALERTS[key] && FINALIZE_ALERTS[key].active) {
          combinedAlerts.push(FINALIZE_ALERTS[key]);
        }
      });
    }

    if (combinedAlerts.length === 0) {
      combinedAlerts.push(FINALIZE_STATE_ALERTS.noAction);
    }

    setAlerts(combinedAlerts);
  }, [patient, reviewRequired, finalizeAlerts, carePlanHasNoGoalsOrInterventions]);

  let lastFinalized;

  if (patient.carePlan?.carePlanId) {
      const isCCstatus = patient.status === USER_BILLING_STATUSES[0];
      let lastFinalizedDate = '';

      if (patient.carePlan?.recentCpDate && isCCstatus) {
        const tenantTimezone = timezone || moment.tz.guess();
        const formattedCpDate = moment(patient.carePlan.recentCpDate, DATE_FORMAT.FULL_SERVER_WITH_TIME);
        const now = moment().tz(tenantTimezone).format(DATE_FORMAT.FULL_SERVER_WITH_TIME);
        const isSameDay = moment(now).isSame(formattedCpDate, 'day');
        const isSameYear = moment(now).isSame(formattedCpDate, 'year');

        if (formattedCpDate.isValid()) {
          if (isSameDay) {
            lastFinalizedDate = `today at ${formattedCpDate.format(TIME_FORMAT.TIME_WITH_TIMEZONE)}`;
          } else if (isSameYear) {
            lastFinalizedDate = `${formattedCpDate.format(DATE_FORMAT.MONTH_WITH_DAY)}`;
          } else {
            lastFinalizedDate = formattedCpDate.format(DATE_FORMAT.FULL_WITH_STING_MONTH);
          }
        }

        lastFinalized = `Last finalized ${lastFinalizedDate}`;
      }
    }

  return (
    <div className="patient-profile h-100 overflow-auto pr-3">
      <h4 className="text-uppercase text-left">FINAL REVIEW</h4>
      <div className="row text-left no-gutters pb-3 mb-3">
        <div className="col-12 mb-2">
          {
            alerts.sort((a, b) => a.id - b.id).map(item => (
              <Alert key={`alert_${item.id}`} variant={item.variant} className="mb-1 py-2 px-3">
                {item.id != 99 && <i className={`bi ${item.icon ? item.icon : 'bi-exclamation-triangle'} mr-2`} />}
                <span className="ml-2" data-test="finalize_alerts">{item.message}</span>
              </Alert>))}
        </div>
        <div className="col-12 ml-3">
          <div className="row m-0">
            <div className="col-12">
              <h5>Reminder</h5>
              <div className="col-12 my-2">
                  Review the patient's chart to ensure all documentation is accurate and complete, then click '+ Add Time Tracking' to log your time. 
                  The Care Plan is automatically sent to the provider when billing is submitted monthly.
              </div>
            </div>
          </div>
          <div className="row no-gutters p-4"/>
          <div className="row m-0">
            <div className="col-12 no-gutters text-right">
              <AddTimeTrackingButton size="md" margin="mr-2" onClose={refetchAlerts} />
            </div>
          </div>
          <div className="col-12 no-gutters mt-2 text-right">
            <span className="text-ccm-gray col-12 mt-2 text-right mr-2" data-test="finalize_lastFinalizedText">{lastFinalized}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Finalize;
